import { useCallback, useEffect } from 'react'
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar'
import { Helmet } from 'react-helmet-async'
import { getPomodoroTimerControls } from '@hooks/use-pomodoro-timer.hook'
import { MoreHoriz, Pause, PlayArrow, Refresh } from '@mui/icons-material'
import { Button } from '@mui/material'
import { AMBIENT_PLAYER_PLAY_EVENT, Chip, ContextualHelp } from '@components'
import { POMODORO_STATUS, TYPES_CHIPS } from '@constants'
import { usePomodoro } from '@hooks'
import { usePomodoroStore } from '@stores'
import { formatDigit } from '@utils'
import { FORM_DATA_INITIAL, ModalComponent } from './partials'
import './pomodoro.style.scss'

const POMODORO_HELP_SUMMARY =
  'Pomodoro \u00e9 um jeito simples de focar sem esquecer de descansar: voc\u00ea trabalha por um tempo, pausa um pouco e volta com mais energia.'

const POMODORO_HELP_ITEMS = [
  {
    title: 'Comece pelo foco',
    description: 'Escolha uma tarefa, aperte play e deixe o timer guiar voc\u00ea.',
  },
  {
    title: 'Pausas importam',
    description:
      'Quando o tempo acabar, respire no intervalo. Depois de alguns focos, vem uma pausa maior.',
  },
  {
    title: 'Seu ritmo',
    description:
      'No bot\u00e3o de tr\u00eas pontos, ajuste os tempos para combinar com o seu dia.',
  },
]

const POMODORO_HELP_TIPS = [
  'Dica: uma tarefa por vez. O timer cuida do resto.',
]

const TIME_FIELDS = ['pomodoro', 'shortBreak', 'longBreak']
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/

const PomodoroScreen = () => {
  const {
    status,
    formData,
    settingsList,
    selectedConfig,
    activePomodoro,
    isModalOpen,
    progressValue,
    timerState,
    setFormData,
    setSettingsList,
    setSelectedConfig,
    setActivePomodoro,
    setIsModalOpen,
    setStatus,
  } = usePomodoroStore()

  const { getPomodoroConfig, createPomodoroConfig, deletePomodoroConfig } =
    usePomodoro()

  const { hours = 0, minutes = 0, seconds = 0 } = timerState ?? {}
  const { handleStart, handlePause, handleRestart } =
    getPomodoroTimerControls()

  const fetchPomodoroConfigs = useCallback(() => {
    const result = getPomodoroConfig()
    if (result?.length) {
      setSettingsList(result)
    }
  }, [getPomodoroConfig, setSettingsList])

  useEffect(() => {
    fetchPomodoroConfigs()
  }, [fetchPomodoroConfigs])

  useEffect(() => {
    if (!selectedConfig && settingsList) {
      const defaultConfig = settingsList.find(
        ({ isVisible }) => isVisible === true
      )

      setSelectedConfig(defaultConfig)
      setActivePomodoro({
        title: 'FOCUS',
        time: defaultConfig.focusTime,
      })
    }
  }, [selectedConfig, settingsList, setActivePomodoro, setSelectedConfig])

  const handleSubmit = ({ isValid, values }) => {
    const hasValidTimes = TIME_FIELDS.every(field =>
      POSITIVE_INTEGER_PATTERN.test(String(values?.[field] ?? ''))
    )

    if (isValid && values && hasValidTimes) {
      const data = {
        categoryName: values.name?.trim() || 'Pomodoro',
        focusTime: Number(values.pomodoro),
        shortBreakTime: Number(values.shortBreak),
        longBreakTime: Number(values.longBreak),
      }

      createPomodoroConfig(data)
      fetchPomodoroConfigs()
      setFormData({ ...FORM_DATA_INITIAL })
    }
  }

  const handleDelete = ({ id, evento }) => {
    evento.stopPropagation()
    deletePomodoroConfig(id)
    fetchPomodoroConfigs()
  }

  const handleSelectConfig = ({
    id,
    categoryName,
    focusTime,
    shortBreakTime,
    longBreakTime,
  }) => {
    setSelectedConfig({
      id,
      categoryName,
      focusTime,
      shortBreakTime,
      longBreakTime,
    })
    setActivePomodoro({ title: 'FOCUS', time: focusTime })
    setStatus(POMODORO_STATUS.INITIAL)
    setIsModalOpen(false)
  }

  const handleChange = event => {
    const { name, value } = event.target
    const isTimeField = TIME_FIELDS.includes(name)

    if (isTimeField && value !== '' && !POSITIVE_INTEGER_PATTERN.test(value)) {
      return
    }

    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        value,
        isValid: !isTimeField || POSITIVE_INTEGER_PATTERN.test(value),
      },
    })
  }

  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const handleStartWithAmbientSound = () => {
    window.dispatchEvent(new Event(AMBIENT_PLAYER_PLAY_EVENT))
    handleStart()
  }

  const buttonConfig =
    status === POMODORO_STATUS.PROGRESS
      ? {
          onClick: handlePause,
          icon: <Pause className="pause__icon" />,
        }
      : {
          onClick: handleStartWithAmbientSound,
          icon: <PlayArrow className="play__icon" />,
        }

  return (
    <>
      <Helmet>
        <title>Pomodoro | To Focus</title>
      </Helmet>

      <section className="pomodoro">
        <div className="pomodoro__topbar">
          <ContextualHelp
            className="pomodoro__help"
            title="Como usar o Pomodoro"
            summary={POMODORO_HELP_SUMMARY}
            items={POMODORO_HELP_ITEMS}
            tips={POMODORO_HELP_TIPS}
          />
        </div>

        <main className="pomodoro__progress-bar">
          <CircularProgressbarWithChildren
            value={progressValue}
            strokeWidth={5}
            styles={buildStyles({
              pathColor: '#F29166',
              trailColor: '#2E7F7B',
              strokeLinecap: 'round',
            })}
          >
            <div className="pomodoro__timer__text">
              {!!hours && <span>{formatDigit(hours)}:</span>}
              <span>{formatDigit(minutes)}</span>:
              <span>{formatDigit(seconds)}</span>
            </div>
          </CircularProgressbarWithChildren>
        </main>

        <div className="pomodoro__cycle">
          <Chip type={TYPES_CHIPS.FOCUS} className={activePomodoro.title} />
          <Chip
            type={TYPES_CHIPS.SHORT_BREAK}
            className={activePomodoro.title}
          />
          <Chip
            type={TYPES_CHIPS.LONG_BREAK}
            className={activePomodoro.title}
          />
        </div>

        <div className="pomodoro__actions">
          <Button
            variant="contained"
            onClick={openModal}
            className="pomodoro__action-button pomodoro__action-button--aux"
            sx={{
              width: 'clamp(52px, 8vmin, 60px)',
              minWidth: 'clamp(52px, 8vmin, 60px)',
              height: 'clamp(52px, 8vmin, 60px)',
              borderRadius: 5,
              opacity: 0.25,
            }}
            color="secondary"
          >
            <MoreHoriz className="dots__icon" />
          </Button>

          <Button
            variant="contained"
            onClick={buttonConfig.onClick}
            className="pomodoro__action-button pomodoro__action-button--main"
            sx={{
              width: 'clamp(82px, 11vmin, 90px)',
              minWidth: 'clamp(82px, 11vmin, 90px)',
              height: 'clamp(74px, 10vmin, 80px)',
              borderRadius: 5,
              margin: 1,
            }}
            color="secondary"
          >
            {buttonConfig.icon}
          </Button>

          <Button
            variant="contained"
            onClick={handleRestart}
            className="pomodoro__action-button pomodoro__action-button--aux"
            sx={{
              width: 'clamp(52px, 8vmin, 60px)',
              minWidth: 'clamp(52px, 8vmin, 60px)',
              height: 'clamp(52px, 8vmin, 60px)',
              borderRadius: 5,
              opacity: 0.25,
            }}
            color="secondary"
          >
            <Refresh className="refresh__icon" />
          </Button>
        </div>
      </section>

      <ModalComponent
        formData={formData}
        handleSubmit={handleSubmit}
        handleChange={handleChange}
        handleClick={handleSelectConfig}
        handleClickDelete={handleDelete}
        handleClickClose={closeModal}
        pomodoroSettingsList={settingsList}
        open={isModalOpen}
      />
    </>
  )
}

export { PomodoroScreen }
